import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { Subject, Subscription, takeUntil, catchError, of, finalize } from 'rxjs';
import { LPRLogsRequestModel, LPRLogVM } from '../../../../models/orderModel';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';

@Component({
  selector: 'app-lpr-logs',
  standalone: false,
  templateUrl: './lpr-logs.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lpr-logs.component.css'
})
export class LprLogsComponent implements OnInit, OnDestroy {
  private orgSubscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  organization: any;
  lprLogs: LPRLogVM[] = [];
  dataSource: MatTableDataSource<LPRLogVM>;

  displayedColumns: string[] = [
    'created',
    'plateText',
    'sensorProviderID',
    'action',
    'stage',
    'handledAt',
    'handledByEventId'
  ];

  allActions: Set<string> = new Set([
    'wrong gate',
    'open gate for staff car',
    'open gate at terminal',
    'open gate at shortterm',
    'checkin - showed QR for driveUp',
    'checkin - too early',
    'checkin - too late',
    'checkin - wrongParking',
    'checkin - subscription',
    'checkout - no order',
    'checkout - wrong time',
    'checkout - trying to access',
    'checkout - checkout',
    'checkout - driveUp',
    'checkout - subscription',
    'parsing error'
  ]);

  @Input() isLoading: boolean = false;
  @Output() incrementRequests = new EventEmitter<void>();
  @Output() decrementRequests = new EventEmitter<void>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    if (sort) {
      this.sort = sort;
      this.setupSorting();
    }
  }
  sort: MatSort;

  totalData: number = 0;
  pageSizes: number[] = [10, 25, 50, 100];

  filtersExpanded: boolean = true;
  selectedSensorIds: string[] = [];
  availableSensors: any[] = [];
  licensePlateSearch: string = '';
  selectedActions: string[] = [];

  startDate: Date | null = null;
  startTime: string = '00:00';
  endDate: Date | null = null;
  endTime: string = '23:59';

  private readonly columnMapping: { [key: string]: string } = {
    'created': 'Created',
    'plateText': 'PlateText',
    'datetime': 'DateTime',
    'cameraSerialNumber': 'CameraSerialNumber',
    'cameraIPAddress': 'CameraIPAddress',
    'sensorProviderID': 'SensorProviderID'
  };

  constructor(
    private backOfficeService: BackOfficeService,
    private backOffice24StatesService: BackOffice24StatesService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(org => {
      this.organization = org;
      if (org?.organizationId) {
        this.loadAvailableSensors();
        this.setLprLogsTableData();
      }
    });
  }

  getOrganizationGates() {
    this.incrementRequests.emit();
    this.backOfficeService.getOrganizationGates(this.organization.organizationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(gates => {
        gates.forEach(gate => {
          if (gate?.cameras?.length > 0) {
            this.availableSensors.push({
              id: gate?.cameras?.[0]?.sensorId,
              type: gate.cameras[0]?.sensorName === "Checkin" ? "Entry" : "Exit",
              name: gate?.cameras?.[0]?.sensorId
            });
          }
        });

        this.decrementRequests.emit();
      });
  }


  // TODO: Implement this method to load available sensors
  loadAvailableSensors(): void {
    this.getOrganizationGates();
  }

  setLprLogsTableData() {
    if (!this.organization) return;

    if (this.paginator) {
      this.paginator.page.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.loadData();
      });
    }

    this.setupSorting();
    this.loadData();
  }

  private setupSorting() {
    if (this.sort) {
      if (!this.sort.active) {
        this.sort.active = 'created';
        this.sort.direction = 'desc';
        this.sort.sortChange.emit({
          active: this.sort.active,
          direction: this.sort.direction
        });
      }

      this.sort.sortChange.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.loadData();
      });
    }
  }

  private loadData() {
    if (!this.organization) return;

    const sortActive = this.sort?.active || 'created';
    const sortDirection = this.sort?.direction || 'desc';
    const sortColumn = this.columnMapping[sortActive] || sortActive;

    let fromDateTime: string | null = null;
    let toDateTime: string | null = null;

    if (this.startDate) {
      const [hours, minutes] = this.startTime.split(':');
      const startDateTime = new Date(this.startDate);
      startDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      fromDateTime = startDateTime.toISOString();
    }

    if (this.endDate) {
      const [hours, minutes] = this.endTime.split(':');
      const endDateTime = new Date(this.endDate);
      endDateTime.setHours(parseInt(hours), parseInt(minutes), 59, 999);
      toDateTime = endDateTime.toISOString();
    }

    const model: LPRLogsRequestModel = {
      sensorIds: this.selectedSensorIds.length > 0 ? this.selectedSensorIds : null,
      lisencePlate: this.licensePlateSearch || null,
      createdAt: {
        From: fromDateTime,
        To: toDateTime
      },
      actions: this.selectedActions.length > 0 ? this.selectedActions : null,
      SortingColumn: sortColumn,
      SortingDirection: sortDirection as 'asc' | 'desc',
      Paginate: {
        PerPage: this.paginator?.pageSize || 10,
        Page: this.paginator ? this.paginator.pageIndex + 1 : 1
      }
    };

    this.incrementRequests.emit();

    this.backOfficeService.getLprLogs(this.organization.organizationId, model).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading LPR logs:', error);
        return of({ data: [], pagination: { total: 0 } });
      }),
      finalize(() => this.decrementRequests.emit())
    ).subscribe((response: any) => {
      if (!response) return;

      this.lprLogs = response.data || [];
      this.totalData = response.pagination?.total || 0;

      if (!this.dataSource) {
        this.dataSource = new MatTableDataSource<LPRLogVM>(this.lprLogs);
      } else {
        this.dataSource.data = this.lprLogs;
      }
    });
  }

  applyFilters(): void {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadData();
  }

  clearFilters(): void {
    this.selectedSensorIds = [];
    this.selectedActions = [];
    this.licensePlateSearch = '';
    this.startDate = null;
    this.startTime = '00:00';
    this.endDate = null;
    this.endTime = '23:59';
    this.applyFilters();
  }

  getActiveFiltersCount(): number {
    let count = 0;
    if (this.selectedSensorIds.length > 0) count++;
    if (this.licensePlateSearch?.trim()) count++;
    if (this.startDate || this.endDate) count++;
    return count;
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  goToLPRLogDetails(log: LPRLogVM) {
    if (!log.id) {
      console.error('Log ID is missing');
      return;
    }
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/admin/lpr-log-details',
        this.organization.organizationId,
        log.id
      ])
    );
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.orgSubscription?.unsubscribe();
  }
}
